import { useMemo, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createColumnHelper, ColumnDef } from "@tanstack/react-table";
import Table from "components/Table";
import { Template } from "types/template-builder-types";
import { getTemplateQuestionsAPI, deleteTemplateAPI } from "api/apiHelper";
import { MdEdit, MdDelete, MdVisibility } from "react-icons/md";
import DeleteModal from "components/DeleteModal";
import { toast } from "react-toastify";

interface TemplatesTableProps {
  templates: Template[];
  isLoading?: boolean;
  onRefresh?: () => void;
}

const TableHeader = ({ children }: { children: React.ReactNode }) => (
  <p className="text-sm font-bold text-gray-600 dark:text-white">{children}</p>
);

const TableCell = ({ children }: { children: React.ReactNode }) => (
  <p className="text-sm font-medium text-navy-700 dark:text-white min-w-[150px] border-white/0 py-3 pr-4">
    {children}
  </p>
);

const TemplatesTable = ({ templates, isLoading, onRefresh }: TemplatesTableProps) => {
  const navigate = useNavigate();
  const columnHelper = createColumnHelper<Template>();
  const [templatesWithCounts, setTemplatesWithCounts] = useState<Template[]>([]);
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; template: Template | null }>({
    isOpen: false,
    template: null,
  });

  useEffect(() => {
    const fetchQuestionCounts = async () => {
      const templatesWithCounts = await Promise.all(
        templates.map(async (template) => {
          try {
            const response = await getTemplateQuestionsAPI(template.id);
            return {
              ...template,
              questions_count: response.data?.length || 0,
            };
          } catch (error) {
            return {
              ...template,
              questions_count: 0,
            };
          }
        })
      );
      setTemplatesWithCounts(templatesWithCounts);
    };

    if (templates.length > 0) {
      fetchQuestionCounts();
    } else {
      setTemplatesWithCounts([]);
    }
  }, [templates]);

  const columns: ColumnDef<Template, any>[] = useMemo(
    () => [
      columnHelper.accessor("id", {
        id: "id",
        header: () => <TableHeader>ID</TableHeader>,
        cell: (info) => (
          <TableCell>
            <span className="font-mono text-xs text-gray-600 dark:text-gray-400">
              #{info.getValue()}
            </span>
          </TableCell>
        ),
      }),
      columnHelper.accessor("name", {
        id: "name",
        header: () => <TableHeader>Name</TableHeader>,
        cell: (info) => (
          <TableCell>
            <span className="font-semibold text-navy-700 dark:text-white">
              {info.getValue()}
            </span>
          </TableCell>
        ),
      }),
      columnHelper.accessor("description", {
        id: "description",
        header: () => <TableHeader>Description</TableHeader>,
        cell: (info) => (
          <TableCell>
            <span className="text-gray-600 dark:text-gray-300 line-clamp-2">
              {info.getValue() || "-"}
            </span>
          </TableCell>
        ),
      }),
      columnHelper.accessor("questions_count", {
        id: "questions_count",
        header: () => <TableHeader>Questions</TableHeader>,
        cell: (info) => (
          <TableCell>
            <span className="px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
              {info.getValue() || 0}
            </span>
          </TableCell>
        ),
      }),
      columnHelper.accessor("is_active", {
        id: "is_active",
        header: () => <TableHeader>Status</TableHeader>,
        cell: (info) => (
          <TableCell>
            <span
              className={`px-2 py-1 rounded-full text-xs font-semibold ${
                info.getValue()
                  ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                  : "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
              }`}
            >
              {info.getValue() ? "Active" : "Inactive"}
            </span>
          </TableCell>
        ),
      }),
      columnHelper.accessor("created_at", {
        id: "created_at",
        header: () => <TableHeader>Created</TableHeader>,
        cell: (info) => (
          <TableCell>
            <span className="text-gray-600 dark:text-gray-400">
              {new Date(info.getValue()).toLocaleDateString()}
            </span>
          </TableCell>
        ),
      }),
      columnHelper.display({
        id: "actions",
        header: () => <TableHeader>Actions</TableHeader>,
        cell: (info) => (
          <TableCell>
            <div className="flex items-center gap-3">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/admin-appraisals/templates/${info.row.original.id}/view`);
                }}
                className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                title="View Template"
              >
                <MdVisibility className="h-5 w-5" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/admin-appraisals/templates/${info.row.original.id}/edit`);
                }}
                className="text-brand-500 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-200 transition-colors"
                title="Edit Template"
              >
                <MdEdit className="h-5 w-5" />
              </button>
              <button
                onClick={(e) => handleDeleteClick(info.row.original, e)}
                className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                title="Delete Template"
              >
                <MdDelete className="h-5 w-5" />
              </button>
            </div>
          </TableCell>
        ),
      }),
    ],
    [columnHelper, navigate]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-gray-500 dark:text-gray-400">Loading templates...</p>
      </div>
    );
  }

  const handleDeleteClick = (template: Template, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteModal({ isOpen: true, template });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.template) return;

    try {
      await deleteTemplateAPI(deleteModal.template.id);
      toast.success("Template deleted successfully");
      setDeleteModal({ isOpen: false, template: null });
      if (onRefresh) {
        onRefresh();
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to delete template";
      toast.error(errorMessage);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ isOpen: false, template: null });
  };

  const displayTemplates = templatesWithCounts.length > 0 ? templatesWithCounts : templates;

  return (
    <>
      <Table
        tableData={displayTemplates}
        columns={columns}
        tableHeading="Templates"
      />
      {deleteModal.isOpen && deleteModal.template && (
        <DeleteModal
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          message={`Are you sure you want to delete the template "${deleteModal.template.name}"? This action cannot be undone.`}
        />
      )}
    </>
  );
};

export default TemplatesTable;
